import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import api from '../../redux/api';

// Backend endpoint that returns the practice's current Stripe subscription total.
// Not available yet — set the URL (e.g. `/stripe/subscription-summary/${practiceId}`)
// when the backend ships it; until then the cost is derived client-side as
// active clinicians × Membership price.
const CURRENT_SUBSCRIPTION_COST_ENDPOINT = '';

/**
 * The practice's current monthly subscription cost, used by the payment modal to
 * show "total cost after new subscriptions are added".
 */
export const useCurrentSubscriptionCost = (practiceId: string, membershipPrice: number | null) => {
  const [endpointCost, setEndpointCost] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const counts = useSelector((state: RootState) => (state as any)?.clinics?.practiceCliniciansCounts);
  const practiceCliniciansRaw = useSelector((state: any) => state?.clinics?.practiceClinicians);
  const practiceClinicians = Array.isArray(practiceCliniciansRaw) ? practiceCliniciansRaw : [];

  useEffect(() => {
    if (!CURRENT_SUBSCRIPTION_COST_ENDPOINT || !practiceId) return;

    let cancelled = false;
    setLoading(true);
    api.get(CURRENT_SUBSCRIPTION_COST_ENDPOINT.replace('{practiceId}', practiceId))
      .then(res => {
        if (cancelled) return;
        const body = res.data?.body || res.data;
        const total = Number(body?.total ?? body?.totalMonthlyCost ?? body?.amount);
        if (!isNaN(total)) setEndpointCost(total);
      })
      .catch(err => console.error('Failed to fetch current subscription cost:', err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [practiceId]);

  if (endpointCost !== null) {
    return { currentMonthlyCost: endpointCost, loading };
  }

  // Derived fallback: active clinicians × Membership price
  if (membershipPrice === null) {
    return { currentMonthlyCost: null, loading };
  }
  const activeCount = typeof counts?.active === 'number'
    ? counts.active
    : practiceClinicians.filter((c: any) => c?.isActive === true).length;

  return { currentMonthlyCost: activeCount * membershipPrice, loading };
};

export default useCurrentSubscriptionCost;
